import { isUserWithinXDays, SarStore } from '@store/sar-store';
import { snapshot } from 'valtio';

export const heading = {
  title: 'What do you need help with?',
  text: 'Select the option that is closest to your issue',
};

export enum Levels {
  Category = 'category',
  Subcategory = 'subcategory',
  Subsubcategory = 'subsubcategory',
  Location = 'location',
}

export enum Warranty {
  CarvanaCareEssential = 'CarvanaCareEssential',
  CarvanaCarePlus = 'CarvanaCarePlus',
  CarvanaCarePremier = 'CarvanaCarePremier',
  CarvanaCareCosmetic = 'CarvanaCareCosmetic',
  LimitedWarranty = 'LimitedWarranty',
  ThirtyDay = 'ThirtyDay',
  SevenDay = 'SevenDay',
  OEMBasic = 'OEMBasic',
  OEMPowerTrain = 'OEMPowerTrain',
  ASRExtension = 'ASRExtension',
}

export enum WarrantyTitle {
  CarvanaCareEssential = 'CarvanaCare Essential',
  CarvanaCarePlus = 'CarvanaCare Plus',
  CarvanaCarePremier = 'CarvanaCare Premier',
  CarvanaCareCosmetic = 'CarvanaCare Cosmetic',
  LimitedWarranty = '100-day Limited Warranty',
  ThirtyDay = '30-day Limited Warranty',
  SevenDay = '7-Day Money Back Guarantee',
  OEMBasic = 'Manufacturer Basic Warranty',
  OEMPowerTrain = 'Manufacturer PowerTrain Warranty',
  ASRExtension = 'ASR Extension',
}

export const anyCarvanaCare = (strToMatch: Warranty | undefined) =>
  strToMatch === Warranty.CarvanaCareEssential ||
  strToMatch === Warranty.CarvanaCarePlus ||
  strToMatch === Warranty.CarvanaCarePremier ||
  strToMatch === Warranty.CarvanaCareCosmetic;

export const anyManufacturer = (strToMatch: Warranty | undefined) =>
  strToMatch === Warranty.OEMBasic || strToMatch === Warranty.OEMPowerTrain;

export enum Category {
  MechanicalAndElectrical = 'Mechanical and electrical',
  Battery = 'Battery',
  HeatingAndCooling = 'Heating and cooling',
  Maintenance = 'Maintenance',
  TiresOrBrakes = 'Tires or brakes',
  Cosmetic = 'Cosmetic',
  Accessories = 'Accessories',
}

export enum SubCategory {
  Electrical = 'Electrical',
  Engine = 'Engine',
  Steering = 'Steering',
  Suspension = 'Suspension',
  Transmission = 'Transmission',
  Gas = 'Gas',
  ElectricOrHybrid = 'Electric / Hybrid',
  HeatingAndCooling = 'Heating and Cooling',
  OilChangeAndOrTireRotation = 'Oil change and/or tire rotation',
  FluidChange = 'Fluid change',
  Other = 'Other',
  Tires = 'Tires',
  Brakes = 'Brakes',
  Wheels = 'Wheels',
  Exterior = 'Exterior',
  Interior = 'Interior',
  Glass = 'Glass',
  KeyNotWorking = "Key isn't working",
  AdditionalKey = 'Additional key',
  MissingFloorMats = 'Missing floor mats',
  MissingOtherAccessories = 'Missing other accessories',
}

export enum SubSubCategory {
  Dents = 'Dents',
  Scratches = 'Scratches',
  Other = 'Other',
  TireDamageYes = 'Yes',
  TireDamageNo = 'No',
}

export enum Location {
  // NEW Exterior
  Door = 'Door',
  Fender = 'Fender',
  FrontBumper = 'Front bumper',
  Grille = 'Grille',
  Hood = 'Hood',
  QuarterPanel = 'Quarter panel',
  RearBumper = 'Rear bumper',
  Roof = 'Roof',
  TrunkTailgate = 'Trunk / Tailgate',
  Wheel = 'Wheel',
  // NEW Interior
  CarpetFloor = 'Carpet / floor',
  CenterConsole = 'Center console',
  Dashboard = 'Dashboard',
  DoorPanel = 'Door panel',
  RoofLiner = 'Roof liner',
  Seat = 'Seat',
  SteeringWheel = 'Steering wheel',
  Trim = 'Trim',
  Other = 'Other',
  // NEW Glass
  Windshield = 'Windshield',
  FrontPassengerWindow = 'Front passenger window',
  FrontDriverWindow = 'Front driver window',
  RearPassengerWindow = 'Rear passenger window',
  RearDriverWindow = 'Rear driver window',
  RearWindow = 'Rear window',
  SunroofMoonroof = 'Sunroof/Moonroof',
  PanoramicRoof = 'Panoramic roof',
}

export const SAR_DATA = (carvanaCareWarranty: Warranty | undefined) => {
  const isWithin7DMBG = isUserWithinXDays(7);
  const { isTreatment } = snapshot(SarStore.paintedDoor);

  return {
    heading: {
      title: isTreatment ? 'Great, now choose a category for your concern' : 'Please share some details',
      text: isTreatment
        ? 'Select the option that best fits your concern. If you have multiple issues, report them one at a time.'
        : "We'll help connect you to a service provider and determine coverage details. For multiple issues, please report each one separately.",
    },
    level: 'category',
    categories: [
      {
        title: Category.MechanicalAndElectrical,
        text: 'Electrical, engine, steering, suspension or transmission',
        heading,
        level: 'subcategory',
        categories: [
          {
            title: SubCategory.Electrical,
            text: 'Vehicle not starting, lights, power windows, power doors, radio or gauges',
            coverage: [
              Warranty.OEMBasic,
              Warranty.LimitedWarranty,
              Warranty.CarvanaCarePlus,
              Warranty.CarvanaCarePremier,
            ],
          },
          {
            title: SubCategory.Engine,
            text: 'Reduced power, vehicle not starting, strange noises, engine stalling or overheating',
            coverage: [
              Warranty.OEMBasic,
              Warranty.OEMPowerTrain,
              Warranty.LimitedWarranty,
              Warranty.CarvanaCareEssential,
              Warranty.CarvanaCarePlus,
              Warranty.CarvanaCarePremier,
            ],
          },
          {
            title: SubCategory.Steering,
            text: 'Difficulty turning, loose steering, vibrating, uneven steering, strange noises or fluid leaks',
            coverage: [
              Warranty.OEMBasic,
              Warranty.LimitedWarranty,
              Warranty.CarvanaCarePlus,
              Warranty.CarvanaCarePremier,
            ],
          },
          {
            title: SubCategory.Suspension,
            text: 'Excessive bouncing, vehicle sagging, uneven tire wear, strange noises or vehicle drifting',
            coverage: [
              Warranty.OEMBasic,
              Warranty.LimitedWarranty,
              Warranty.CarvanaCarePlus,
              Warranty.CarvanaCarePremier,
            ],
          },
          {
            title: SubCategory.Transmission,
            text:
              'Difficulty shifting gears, strange noises, leaking fluids, transmission overheating or check engine light is on',
            coverage: [
              Warranty.OEMBasic,
              Warranty.OEMPowerTrain,
              Warranty.LimitedWarranty,
              Warranty.CarvanaCareEssential,
              Warranty.CarvanaCarePlus,
              Warranty.CarvanaCarePremier,
            ],
          },
        ],
      },
      {
        title: Category.Battery,
        text: 'Includes battery replacement',
        heading: {
          title: 'What type of vehicle is this?',
        },
        level: 'subcategory',
        categories: [
          {
            title: SubCategory.Gas,
            coverage: [Warranty.ThirtyDay],
          },
          {
            title: SubCategory.ElectricOrHybrid,
            coverage: [
              Warranty.OEMPowerTrain,
              Warranty.ThirtyDay,
              Warranty.LimitedWarranty,
              Warranty.CarvanaCareEssential,
              Warranty.CarvanaCarePlus,
              Warranty.CarvanaCarePremier,
            ],
          },
        ],
      },
      {
        title: Category.HeatingAndCooling,
        text: 'Weak heating/cooling, no airflow, etc.',
        level: 'subcategory',
        coverage: [Warranty.OEMBasic, Warranty.LimitedWarranty, Warranty.CarvanaCarePlus, Warranty.CarvanaCarePremier],
        // No "Sub" Categories - defaults to SubCategory value of HeatingAndCooling
      },
      {
        title: Category.Maintenance,
        text: 'Oil change, tire rotation, fluid changes, etc.',
        heading: {
          title: 'What maintenance service are you looking for?',
        },
        level: 'subcategory',
        categories: [
          {
            title: SubCategory.OilChangeAndOrTireRotation,
            coverage: !isWithin7DMBG ? [Warranty.CarvanaCarePremier, Warranty.CarvanaCareCosmetic] : [],
          },
          { title: SubCategory.FluidChange },
          { title: SubCategory.Other },
        ],
      },
      {
        title: Category.Accessories,
        text: 'Keys, floor mats, front license bracket, etc.',
        heading,
        level: 'subcategory',
        categories: [
          {
            title: SubCategory.KeyNotWorking,
            coverage: [Warranty.OEMBasic, Warranty.LimitedWarranty],
          },
          { title: SubCategory.AdditionalKey },
          { title: SubCategory.MissingFloorMats, coverage: [Warranty.SevenDay] },
          { title: SubCategory.MissingOtherAccessories, coverage: [Warranty.SevenDay] },
        ],
      },
      {
        title: Category.TiresOrBrakes,
        text: 'Damaged tires or brakes.',
        heading,
        level: 'subcategory',
        categories: [
          {
            title: SubCategory.Tires,
            coverage: [Warranty.ThirtyDay],
            ...addRoadHazardScreenForPremierAndCosmetic(carvanaCareWarranty),
          },
          {
            title: SubCategory.Brakes,
            coverage: [
              Warranty.ThirtyDay,
              Warranty.LimitedWarranty,
              Warranty.CarvanaCarePlus,
              Warranty.CarvanaCarePremier,
              Warranty.CarvanaCareCosmetic,
            ],
          },
          {
            title: SubCategory.Wheels,
            coverage: [Warranty.ThirtyDay],
            ...addRoadHazardScreenForPremierAndCosmetic(carvanaCareWarranty),
          },
        ],
      },
      {
        title: Category.Cosmetic,
        text: 'Dents, scratches, stains, cracks or issues with your glass',
        heading,
        level: 'subcategory',
        categories: [
          {
            title: SubCategory.Exterior,
            text: 'Scratches, dents, paint chips or rust spots',
            heading,
            level: 'subsubcategory',
            coverage: [Warranty.SevenDay, Warranty.ASRExtension],
            categories: [
              {
                title: SubSubCategory.Dents,
                coverage: [Warranty.CarvanaCarePremier, Warranty.CarvanaCareCosmetic],
              },
              {
                title: SubSubCategory.Scratches,
              },
              {
                title: SubSubCategory.Other,
              },
            ],
            locations: [
              { title: Location.Door },
              { title: Location.Fender },
              { title: Location.FrontBumper },
              { title: Location.Grille },
              { title: Location.Hood },
              { title: Location.QuarterPanel },
              { title: Location.RearBumper },
              { title: Location.Roof },
              { title: Location.TrunkTailgate },
              { title: Location.Wheel },
              { title: Location.Other },
            ],
          },
          {
            title: SubCategory.Interior,
            text: 'Tears, stains or cracks',
            level: 'subcategory',
            coverage: [Warranty.SevenDay, Warranty.ASRExtension],
            locations: [
              { title: Location.CarpetFloor },
              { title: Location.CenterConsole },
              { title: Location.Dashboard },
              { title: Location.DoorPanel },
              { title: Location.RoofLiner },
              { title: Location.Seat },
              { title: Location.SteeringWheel },
              { title: Location.Trim },
              { title: Location.Other },
            ],
          },
          {
            title: SubCategory.Glass,
            text: 'Chips, cracks or scratches',
            level: 'subcategory',
            heading: {
              title: 'Where is the glass issue located?',
              text: 'Select the location that best matches your concern',
            },
            locations: [
              { title: Location.Windshield },
              { title: Location.FrontPassengerWindow },
              { title: Location.FrontDriverWindow },
              { title: Location.RearPassengerWindow },
              { title: Location.RearDriverWindow },
              { title: Location.RearWindow },
              { title: Location.SunroofMoonroof },
              { title: Location.PanoramicRoof },
            ],
          },
        ],
      },
    ],
  };
};

const addRoadHazardScreenForPremierAndCosmetic = (warranty: Warranty | undefined) => {
  if (!warranty) return null;

  const isPremier = warranty === Warranty.CarvanaCarePremier;
  const isCosmetic = warranty === Warranty.CarvanaCareCosmetic;

  if (isPremier || isCosmetic) {
    return {
      heading: {
        title: 'Was your issue caused by road debris, a curb or pothole?',
        text:
          'Examples include flat tires, blow outs, curb rash or debris-related damage. We do not cover normal wear or tear.',
      },
      categories: [
        {
          title: SubSubCategory.TireDamageYes,
          coverage: [Warranty.CarvanaCarePremier, Warranty.CarvanaCareCosmetic],
        },
        {
          title: SubSubCategory.TireDamageNo,
        },
      ],
    };
  }

  return null;
};
